import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  Platform,
} from 'react-native';
import Svg, { Rect, G } from 'react-native-svg';
import { Theme, ThemeConsumer } from '@rneui/themed';
import { ZenBadge, ZenButton, ZenIcon, ZenText, ZenToast } from 'zen-core-elements';
import { appColors, appFonts } from '../../../../themes/appTheme';
import Clipboard from '@react-native-clipboard/clipboard';
import RNPrint from "react-native-print";
import {
  generateBarcodeDetails,
  BarcodeResult,
} from '../../../../utils/orderReceiptGenerator';

export type BarcodeFormat = 'CODE128' | 'EAN13';


import { fieldBoxProps, fieldInputProps, webFieldBox } from '../../../../platform/design/searchFieldWeb';
import useProductSettings from '../../../../shared/hooks/useProductSettings';
import { getNextSku, previewSku } from '../../../../utils/productSettingsStorage';
import { ProductLike } from '../../../settings/productSettings.module';

export interface ProductBarcodeGeneratorProps {
  /**
   * Product SKU (Stock Keeping Unit). Required.
   */
  sku: string;
  /**
   * Universal Product Code / EAN (13-digit numeric barcode). Optional.
   */
  upc?: string;
  /**
   * Product display name. Optional.
   */
  productName?: string;
  /**
   * Category name. Its first 3 letters become the category token when that
   * token is switched on in Product Settings. Optional.
   */
  categoryName?: string;
  /**
   * Brand name, used for the brand token. Optional.
   */
  brandName?: string;
  /**
   * Variant name, used for the variant token. Optional.
   */
  variantName?: string;
  /**
   * Callback fired when SKU is edited or auto-generated and successfully validated.
   */
  onSkuChange?: (newSku: string) => void;
  /**
   * Flag to disable SKU editing (e.g., in read-only permission mode).
   */
  disabled?: boolean;
  /**
   * True when the product already carries a saved SKU. The field is then shown
   * locked and auto generation never runs, so a code that is already printed on
   * stock can not be changed.
   */
  readOnly?: boolean;
}

/**
 * Validates whether a string is non-empty and contains valid printable ASCII characters for CODE128 encoding.
 */
export const isValidCode128 = (code?: string | null): boolean => {
  if (!code || code.trim().length === 0) return false;
  // Standard CODE128 printable ASCII range (hex 0x20 to 0x7E, decimal 32 to 126)
  return /^[\x20-\x7E]+$/.test(code.trim());
};

/**
 * The UPC is a free text field, so it is only drawn as an EAN-13 barcode when
 * the encoder actually accepts it. Anything else falls back to the SKU /
 * CODE128 barcode instead of reporting an error on the field.
 */
const isEan13Encodable = (code?: string | null): boolean => {
  const trimmed = code?.trim();
  if (!trimmed || !/^d{13}$/.test(trimmed)) return false;
  return generateBarcodeDetails(trimmed, 10, 1, 'EAN13') !== null;
};

export const ProductBarcodeGenerator: React.FC<ProductBarcodeGeneratorProps> = ({
  sku,
  upc,
  productName,
  categoryName,
  brandName,
  variantName,
  onSkuChange,
  disabled = false,
  readOnly = false,
}) => {
  const { width } = useWindowDimensions();

  // Auto SKU is driven entirely by Settings > Products, and never touches a
  // product that already has a saved SKU.
  const { settings } = useProductSettings();
  const autoSkuEnabled = settings.sku.enabled && !disabled && !readOnly;
  const isSkuEditable = !disabled && !readOnly;
  // Internal state for the editable SKU text field
  const [skuInput, setSkuInput] = useState<string>(sku || '');
  // Debounced SKU value used for live barcode generation
  const [debouncedSku, setDebouncedSku] = useState<string>(sku || '');
  // A SKU that already exists belongs to the product; never overwrite it.
  const [isManualSku, setIsManualSku] = useState<boolean>(() => Boolean(sku && sku.trim().length > 0));
  /**
   * Sequence number reserved for this product/variant. It is taken from the
   * shared counter exactly once; later edits to the name, category or brand
   * rebuild the SKU around the same number instead of consuming another.
   */
  const [reservedSequence, setReservedSequence] = useState<number | null>(null);

  const skuSource: ProductLike = useMemo(
    () => ({ productName, categoryName, brandName, variantName }),
    [productName, categoryName, brandName, variantName],
  );

  // Track the last successfully validated and rendered barcode value & format
  const [lastValidBarcode, setLastValidBarcode] = useState<{
    value: string;
    format: BarcodeFormat;
  }>(() => {
    if (isEan13Encodable(upc)) {
      return { value: upc!.trim(), format: 'EAN13' };
    }
    const initialSku = sku && isValidCode128(sku) ? sku.trim() : 'SAMPLE-SKU';
    return { value: initialSku, format: 'CODE128' };
  });

  // Validation error message for inline display
  const [validationError, setValidationError] = useState<string | null>(null);
  // Track barcode rendering encoding error from Barcode component
  const [hasEncodingError, setHasEncodingError] = useState<boolean>(false);
  // Fullscreen high-contrast scanner modal state
  // const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  // Track input focus state for active styling
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoSkuTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync internal input when external sku prop changes (e.g. from parent form reset / fetch)
  useEffect(() => {
    if (sku !== undefined && sku !== skuInput) {
      setSkuInput(sku || '');
      setDebouncedSku(sku || '');
      if (isValidCode128(sku)) {
        setValidationError(null);
        setHasEncodingError(false);
        if (!isEan13Encodable(upc)) {
          setLastValidBarcode({ value: sku.trim(), format: 'CODE128' });
        }
      }
    }
  }, [sku]);

  // If external UPC changes and becomes encodable, update last valid barcode
  useEffect(() => {
    if (isEan13Encodable(upc)) {
      setHasEncodingError(false);
      setLastValidBarcode({ value: upc!.trim(), format: 'EAN13' });
    }
  }, [upc]);

  // Applies a generated SKU to the field, the barcode preview and the parent form.
  const applyGeneratedSku = useCallback(
    (generated: string) => {
      if (!generated || generated === skuInput) return;

      setSkuInput(generated);
      setDebouncedSku(generated);
      setValidationError(null);
      setHasEncodingError(false);

      if (!isEan13Encodable(upc)) {
        setLastValidBarcode({ value: generated, format: 'CODE128' });
      }

      onSkuChange?.(generated);
    },
    [skuInput, upc, onSkuChange],
  );

  /**
   * Auto-SKU effect. The first run reserves a sequence number from the shared
   * counter; every later run rebuilds the SKU around that same number, so
   * editing the product name or category never burns another number.
   */
  useEffect(() => {
    if (!autoSkuEnabled || isManualSku) return;
    // Nothing to build a code from yet - avoids reserving a number the moment
    // an empty Add Product form opens.
    if (!productName?.trim()) return;

    if (autoSkuTimerRef.current) {
      clearTimeout(autoSkuTimerRef.current);
    }

    let cancelled = false;

    autoSkuTimerRef.current = setTimeout(() => {
      if (reservedSequence !== null) {
        applyGeneratedSku(previewSku(settings, skuSource, reservedSequence));
        return;
      }

      getNextSku(skuSource, settings)
        .then(({ sku: generated, sequence }) => {
          if (cancelled) return;
          setReservedSequence(sequence);
          applyGeneratedSku(generated);
        })
        .catch(() => {
          if (!cancelled) {
            ZenToast({ type: 'error', message: 'Unable to generate a SKU' });
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      if (autoSkuTimerRef.current) {
        clearTimeout(autoSkuTimerRef.current);
      }
    };
  }, [autoSkuEnabled, isManualSku, productName, skuSource, settings, reservedSequence, applyGeneratedSku]);

  // Manual typing handler for SKU input
  const handleSkuInputChange = (text: string) => {
    setSkuInput(text);

    // If user typed non-empty text, mark as manual override
    // If user completely clears the field, reset manual flag so auto-SKU can resume
    if (text.trim().length === 0) {
      setIsManualSku(false);
    } else {
      setIsManualSku(true);
    }

    // If there is an active error, re-check lightly to clear error if valid
    if (validationError && isValidCode128(text)) {
      setValidationError(null);
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSku(text);
      if (isValidCode128(text)) {
        setHasEncodingError(false);
        if (!isEan13Encodable(upc)) {
          setLastValidBarcode({ value: text.trim(), format: 'CODE128' });
        }
      }
    }, 200);
  };

  // Clean up debounce timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (autoSkuTimerRef.current) {
        clearTimeout(autoSkuTimerRef.current);
      }
    };
  }, []);

  /**
   * Explicit user action: drops any manual override and takes a fresh number
   * from the counter. This is the only path that consumes a second number.
   */
  const handleRegenerateAutoSku = useCallback(async () => {
    if (!autoSkuEnabled) return;

    try {
      const { sku: generated, sequence } = await getNextSku(skuSource, settings);

      setIsManualSku(false);
      setReservedSequence(sequence);
      applyGeneratedSku(generated);

      ZenToast({
        type: 'success',
        message: `Auto-generated SKU: ${generated}`,
      });
    } catch {
      ZenToast({
        type: 'error',
        message: 'Cannot auto-generate SKU. Please try again.',
      });
    }
  }, [autoSkuEnabled, skuSource, settings, applyGeneratedSku]);

  /**
   * Determine active barcode value, format, and display label:
   * 1. Use the UPC when it can be encoded as a 13-digit EAN-13 barcode -> format "EAN13".
   * 2. Otherwise fall back to the debounced SKU with format "CODE128".
   */
  const barcodeConfig = useMemo(() => {
    // Prefer the UPC whenever it can be drawn as an EAN-13 barcode
    if (isEan13Encodable(upc)) {
      return {
        value: upc!.trim(),
        format: 'EAN13' as BarcodeFormat,
        isUpc: true,
        canRender: true,
        badgeText: 'UPC / EAN-13 (13-Digit)',
        badgeColor: appColors.badgeColor,
      };
    }

    // Fallback to debounced SKU
    const currentCode = debouncedSku?.trim();
    if (isValidCode128(currentCode)) {
      return {
        value: currentCode,
        format: 'CODE128' as BarcodeFormat,
        isUpc: false,
        canRender: true,
        badgeText: 'CODE128',
        badgeColor: appColors.primary,
      };
    }

    // Fallback to last valid barcode if current debounced value is invalid/empty
    const fallbackVal = lastValidBarcode.value?.trim();
    const canRenderFallback =
      lastValidBarcode.format === 'EAN13' ? isEan13Encodable(fallbackVal) : isValidCode128(fallbackVal);

    return {
      value: fallbackVal || 'SAMPLE-SKU',
      format: lastValidBarcode.format,
      isUpc: lastValidBarcode.format === 'EAN13',
      canRender: canRenderFallback,
      badgeText: lastValidBarcode.format === 'EAN13' ? 'UPC / EAN-13' : 'CODE128 (Fallback)',
      badgeColor: appColors.pendingColor,
    };
  }, [upc, debouncedSku, lastValidBarcode]);

  /**
   * Validate the SKU input on blur or explicit save:
   * - Must not be empty.
   * - Must only contain characters valid for CODE128.
   * - Fires onSkuChange callback with finalized value.
   */
  const handleFinalizeSku = useCallback(() => {
    const trimmed = skuInput.trim();

    if (!trimmed) {
      setValidationError('SKU is required and cannot be empty.');
      return;
    }

    if (!isValidCode128(trimmed)) {
      setValidationError('SKU contains invalid characters for CODE128 encoding.');
      return;
    }

    // Valid SKU: clear errors and persist
    setValidationError(null);
    setHasEncodingError(false);

    if (!isEan13Encodable(upc)) {
      setLastValidBarcode({ value: trimmed, format: 'CODE128' });
    }

    if (onSkuChange && trimmed !== sku) {
      onSkuChange(trimmed);
    }
  }, [skuInput, sku, upc, onSkuChange]);

  /**
   * Quick action to copy the current barcode string
   */
  const handleCopyCode = useCallback(() => {
    const codeToCopy = barcodeConfig.value;
    if (!codeToCopy) return;
    Clipboard.setString(codeToCopy);

    ZenToast({
      type: 'success',
      message: `Barcode (${codeToCopy}) copied to clipboard`,
    });
  }, [barcodeConfig.value]);

  const cardBarcodeWidth = Math.min(width * 0.75, 260);

  // Generate vector barcode data using orderReceiptGenerator logic
  const barcodeResult: BarcodeResult | null = useMemo(() => {
    if (!barcodeConfig.canRender || !barcodeConfig.value) return null;
    return generateBarcodeDetails(barcodeConfig.value, 65, 1.8, barcodeConfig.format);
  }, [barcodeConfig.value, barcodeConfig.format, barcodeConfig.canRender]);

  const handlePrintBarcode = async () => {
    try {
      const printValue = barcodeConfig.value;
      if (!printValue) {
        ZenToast({
          type: 'error',
          message: 'No barcode value to print',
        });
        return;
      }

      // Generate sharp vector barcode for label printing (moduleWidth: 2.8, height: 130)
      const printDetails = generateBarcodeDetails(printValue, 160, 3, barcodeConfig.format);
      if (!printDetails) {
        ZenToast({
          type: 'error',
          message: 'Failed to generate barcode for printing',
        });
        return;
      }

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${productName || 'Product Barcode'}</title>
          <style>
            @page {
              size: 80mm 45mm;
              margin: 0;
            }
            *, *:before, *:after {
              box-sizing: border-box;
            }
            html, body {
              width: 80mm;
              margin: 0;
              padding: 0;
              background-color: #ffffff;
              font-family: Arial, Helvetica, sans-serif;
              text-align: center;
              overflow: hidden;
            }
            .labelContainer {
              width: 80mm;
              margin: 0 auto;
              padding: 2mm 3mm;
              box-sizing: border-box;
              text-align: center;
              background-color: #ffffff;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            }
            .productTitle {
              font-size: 16px;
              font-weight: 700;
              color: #000000;
              margin: 0 0 3px 0;
              max-width: 95%;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              line-height: 1.2;
            }
            .barcodeSvgWrapper {
              width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              margin: 2px 0;
            }
            .barcodeSvgWrapper svg {
              max-width: 94%;
              height: auto;
              max-height: 25mm;
              display: block;
              margin: 0 auto;
            }
            .barcodeCodeText {
              font-size: 16px;
              font-weight: 800;
              color: #000000;
              letter-spacing: 2.5px;
              margin: 3px 0 0 0;
              line-height: 1.2;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="labelContainer">
            ${productName ? `<div class="productTitle">${productName}</div>` : ''}
            <div class="barcodeSvgWrapper">
              ${printDetails.svg}
            </div>
            <div class="barcodeCodeText">${printValue}</div>
          </div>
        </body>
        </html>
      `;

      await RNPrint.print({
        html,
      });
    } catch (err: any) {
      console.error('Failed to print barcode:', err);
      ZenToast({
        type: 'error',
        message: 'Failed to print barcode',
      });
    }
  };

  /**
   * Handle barcode rendering error from library asynchronously to avoid setState during render
   */
  const handleBarcodeError = useCallback((error: any) => {


    if (__DEV__) {
      console.warn('Barcode encoding warning:', error?.message || error);
    }
    // Defer state update so it never executes during another component's render phase
    setTimeout(() => {
      setHasEncodingError(true);
    }, 0);
  }, []);

  /** Shape the next auto SKU would take, for the placeholder and helper line. */
  const autoSkuPreview = useMemo(
    () => (autoSkuEnabled ? previewSku(settings, skuSource, reservedSequence ?? undefined) : ''),
    [autoSkuEnabled, settings, skuSource, reservedSequence],
  );

  return (
    <ThemeConsumer>
      {({ theme }: { theme: Theme }) => {
        const isErrorState = !!validationError || hasEncodingError || (!barcodeResult && barcodeConfig.canRender);

        return (
          <View style={[styles.cardContainer, { borderColor: theme?.borderColor || appColors.borderColor }]}>
            {/* Header / Info Row */}
            <View style={styles.headerRow}>
              <View style={styles.headerLeft}>
                <ZenIcon
                  name="barcode-scan"
                  type="material-community"
                  size={20}
                  color={theme?.primary || appColors.primary}
                />
                <ZenText
                  content={productName ? `${productName}` : 'Product Barcode'}
                  bold
                  size={theme?.smallHeaderFontSize || 15}
                  color={theme?.dark || appColors.dark}
                  style={styles.headerTitle}
                  numberOfLines={1}
                />
              </View>

              {/* Format Badge */}
              {/* <ZenBadge
                badgeName={barcodeConfig.badgeText}
                badgeColor={barcodeConfig.badgeColor}
                badgeTextColor={theme?.light || appColors.light}
                badgeTextSize="small"
                badgeTextStyle={{ fontFamily: theme?.bold || appFonts.bold, fontSize: 11 }}
                badgeContainerStyle={styles.badgeContainer}
              /> */}
            </View>

            {/* Centered Barcode Preview Canvas */}
            <View style={styles.barcodeVisualCard}>
              {barcodeResult && barcodeResult.bars.length > 0 ? (
                <View style={styles.barcodeWrapper}>
                  <Svg
                    width={cardBarcodeWidth}
                    height={65}
                    viewBox={`0 0 ${barcodeResult.totalWidth} ${barcodeResult.height}`}
                  >
                    <Rect width="100%" height="100%" fill="#ffffff" />
                    <G fill="#000000">
                      {barcodeResult.bars.map((bar, index) => (
                        <Rect
                          key={index}
                          x={bar.x}
                          y={0}
                          width={bar.width}
                          height={barcodeResult.height}
                        />
                      ))}
                    </G>
                  </Svg>
                  {/* Human readable string below barcode */}
                  <ZenText
                    content={barcodeConfig.value}
                    color={appColors.dark}
                    size={theme?.smallFontSize || 13}
                    style={styles.humanReadableText}
                    bold
                  />
                </View>
              ) : (
                /* Graceful Fallback if barcode cannot be encoded */
                <View style={styles.fallbackContainer}>
                  <ZenIcon
                    name="alert-circle-outline"
                    type="material-community"
                    size={28}
                    color={appColors.warning || '#f5a623'}
                  />
                  <ZenText
                    content="Preview Unavailable"
                    size={theme?.smallFontSize || 12}
                    color={appColors.lightDark}
                    bold
                    style={{ marginTop: 4 }}
                  />
                  <ZenText
                    content={barcodeConfig.value || 'Invalid or Empty Code'}
                    size={theme?.badgeFontSize || 11}
                    color={appColors.lightDark}
                  />
                </View>
              )}
            </View>

            {/* Action Bar: Copy & Fullscreen Modal Scanner */}
            <View style={styles.actionRow}>
              <TouchableOpacity
                style={[styles.actionBtn, { borderColor: theme?.borderColor || appColors.borderColor }]}
                onPress={handleCopyCode}
                activeOpacity={0.7}
              >
                <ZenIcon
                  name="content-copy"
                  type="material-community"
                  size={16}
                  color={theme?.primary || appColors.primary}
                />
                <ZenText
                  content="Copy Code"
                  size={12}
                  bold
                  color={theme?.primary || appColors.primary}
                  style={styles.actionBtnText}
                />
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.actionBtn,
                  styles.scanBtn,
                  { backgroundColor: theme?.tealPrimary || appColors.tealPrimary, borderColor: theme?.primary || appColors.primary },
                ]}
                onPress={() => handlePrintBarcode()}
                // onPress={() => setIsModalVisible(true)}
                activeOpacity={0.7}
              >
                <ZenIcon
                  name="printer"
                  type="feather"
                  size={18}
                  color={theme?.primary || appColors.primary}
                />
                <ZenText
                  content="Print Code"
                  size={12}
                  bold
                  color={theme?.primary || appColors.primary}
                  style={styles.actionBtnText}
                />
              </TouchableOpacity>
            </View>

            {/* Editable SKU Input Section */}
            <View style={styles.skuSection}>
              <View style={styles.skuLabelRow}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <ZenText
                    content="SKU"
                    style={Platform.OS === 'web' && theme?.addEditProductStyle?.webFieldLabel}
                    size={13}
                    bold
                    color={theme?.labelColor || appColors.labelColor}
                  />
                  {readOnly ? (
                    <View style={styles.manualBadge}>
                      <ZenText content="Locked" size={10} bold color="#6B7280" />
                    </View>
                  ) : isManualSku ? (
                    <View style={styles.manualBadge}>
                      <ZenText content="Manual" size={10} bold color="#6B7280" />
                    </View>
                  ) : autoSkuEnabled ? (
                    <View style={styles.autoBadge}>
                      <ZenText content="Auto-Generated" size={10} bold color="#0284C7" />
                    </View>
                  ) : null}
                </View>

                {/* Only offered while auto SKU is switched on in Product Settings */}
                {autoSkuEnabled && (
                  <TouchableOpacity
                    style={styles.regenerateBtn}
                    onPress={handleRegenerateAutoSku}
                    activeOpacity={0.7}
                  >
                    <ZenIcon
                      name="refresh"
                      type="material-community"
                      size={13}
                      color={theme?.primary || appColors.primary}
                    />
                    <ZenText
                      content="Reset to Auto-SKU"
                      size={11}
                      bold
                      color={theme?.primary || appColors.primary}
                      style={{ marginLeft: 3 }}
                    />
                  </TouchableOpacity>
                )}
              </View>

              {/* Input Box with Edit indicator and Done/Save button */}
              <View
                {...fieldBoxProps}
                style={[
                  styles.inputWrapper,
                  webFieldBox,
                  {
                    borderColor: isErrorState
                      ? appColors.red
                      : isFocused
                        ? theme?.primary || appColors.primary
                        : theme?.borderColor || appColors.borderColor,
                    backgroundColor: isSkuEditable ? theme?.light || appColors.light : '#f9f9f9',
                  },
                ]}
              >
                <ZenIcon
                  name={readOnly ? 'lock-outline' : 'pencil-outline'}
                  type="material-community"
                  size={18}
                  color={isFocused ? theme?.primary || appColors.primary : appColors.icon}
                  style={styles.inputLeftIcon}
                />

                <TextInput
                  value={skuInput}
                  onChangeText={handleSkuInputChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => {
                    setIsFocused(false);
                    handleFinalizeSku();
                  }}
                  editable={isSkuEditable}
                  accessibilityLabel="SKU"
                  accessibilityState={{ disabled: !isSkuEditable }}
                  placeholder={autoSkuPreview ? `Auto: ${autoSkuPreview}` : 'Enter SKU'}
                  placeholderTextColor={appColors.dropdownPlaceHolder || '#9ea8b1'}
                  style={[
                    styles.textInput,
                    {
                      color: isSkuEditable ? appColors.dark : appColors.disableTextColor,
                      fontFamily: theme?.medium || appFonts.medium,
                    },
                  ]}
                  {...fieldInputProps}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  returnKeyType="done"
                  onSubmitEditing={handleFinalizeSku}
                />
              </View>

              {/* Inline Validation Error Message or Helper Info */}
              {validationError ? (
                <View style={styles.errorRow}>
                  <ZenIcon name="alert-circle" type="material-community" size={14} color={appColors.red} />
                  <ZenText
                    content={validationError}
                    color={appColors.red}
                    size={11}
                    style={styles.errorText}
                  />
                </View>
              ) : readOnly ? (
                <ZenText
                  content="This product already has a SKU, so it cannot be changed."
                  color={appColors.labelColor}
                  size={11}
                  style={styles.helperText}
                />
              ) : !settings.sku.enabled ? (
                <ZenText
                  content="Auto SKU generation is off. Turn it on in Settings > Products."
                  color={appColors.labelColor}
                  size={11}
                  style={styles.helperText}
                />
              ) : !productName?.trim() ? (
                <ZenText
                  content="Auto-SKU waits for: Product Name"
                  color={appColors.labelColor}
                  size={11}
                  style={styles.helperText}
                />
              ) : (
                <ZenText
                  content={`Pattern: ${autoSkuPreview || 'set in Settings > Products'}`}
                  color={appColors.labelColor}
                  size={11}
                  style={styles.helperText}
                />
              )}
            </View>

            {/* High-Contrast Fullscreen Scanning Modal */}
            {/* <Modal
              visible={isModalVisible}
              transparent
              animationType="fade"
              onRequestClose={() => setIsModalVisible(false)}
            >
              <Pressable
                style={styles.modalBackdrop}
                onPress={() => setIsModalVisible(false)}
              >
                <Pressable
                  style={[
                    styles.modalContent,
                    {
                      width: Math.min(width * 0.92, 450),
                      backgroundColor: '#FFFFFF',
                    },
                  ]}
                  onPress={(e) => e.stopPropagation()}
                >
                  <View style={styles.modalHeader}>
                    <View style={{ flex: 1 }}>
                      <ZenText
                        content={productName ? productName : 'Product Barcode'}
                        bold
                        size={17}
                        color="#111827"
                        numberOfLines={1}
                      />

                    </View>

                    <TouchableOpacity
                      onPress={() => setIsModalVisible(false)}
                      style={styles.modalCloseBtn}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                      <ZenIcon name="close" type="material-community" size={24} color="#4B5563" />
                    </TouchableOpacity>
                  </View>

                  <View style={styles.modalDivider} />

                  <View style={styles.modalBarcodeCanvas}>
                    {!hasEncodingError && barcodeConfig.canRender && barcodeConfig.value ? (
                      <View style={{ alignItems: 'center' }}>
                        <Barcode
                          value={barcodeConfig.value}
                          format={barcodeConfig.format}
                          height={120}
                          maxWidth={modalBarcodeWidth}
                          width={2.2}
                          lineColor="#000000"
                          background="#FFFFFF"
                          onError={handleBarcodeError}
                        />
                        <ZenText
                          content={barcodeConfig.value}
                          bold
                          size={18}
                          color="#000000"
                          style={styles.modalCodeText}
                        />
                      </View>
                    ) : (
                      <View style={styles.fallbackContainer}>
                        <ZenIcon
                          name="alert-circle-outline"
                          type="material-community"
                          size={36}
                          color="#EF4444"
                        />
                        <ZenText
                          content="Barcode Cannot Be Encoded"
                          size={14}
                          color="#111827"
                          bold
                          style={{ marginTop: 6 }}
                        />
                        <ZenText
                          content={barcodeConfig.value || 'Invalid format or characters'}
                          size={12}
                          color="#6B7280"
                        />
                      </View>
                    )}
                  </View>

                  {/* <View style={styles.modalMetaRow}>
                    <ZenBadge
                      badgeName={`Format: ${barcodeConfig.badgeText}`}
                      badgeColor={barcodeConfig.badgeColor}
                      badgeTextColor="#FFFFFF"
                      badgeTextSize="small"
                      badgeTextStyle={{ fontFamily: appFonts.bold, fontSize: 12 }}
                      badgeContainerStyle={{ paddingHorizontal: 10, paddingVertical: 4 }}
                    />
                    <ZenText
                      content={barcodeConfig.isUpc ? 'UPC Source' : 'SKU Source'}
                      size={12}
                      color="#6B7280"
                      bold
                    />
                  </View>

                  <View style={styles.modalActions}>
                    <ZenButton
                      title="Copy Code"
                      type="outline"
                      outlineColor={theme?.primary || appColors.primary}
                      titleColor={theme?.primary || appColors.primary}
                      buttonStyle={{ borderRadius: 8, paddingVertical: 10 }}
                      containerStyle={{ flex: 1, marginRight: 8 }}
                      onPress={() => {
                        handleCopyCode();
                      }}
                    />
                    <ZenButton
                      title="Print"
                      buttonColor={theme?.primary || appColors.primary}
                      titleColor="#FFFFFF"
                      buttonStyle={{ borderRadius: 8, paddingVertical: 10 }}
                      containerStyle={{ flex: 1, marginLeft: 8 }}
                      onPress={() => { }}
                    />
                  </View>
                </Pressable>
              </Pressable>
            </Modal> */}
          </View>
        );
      }}
    </ThemeConsumer>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginVertical: 10,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 6,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 8,
  },
  headerTitle: {
    marginLeft: 8,
    flex: 1,
  },
  badgeContainer: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  barcodeVisualCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingVertical: 14,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 110,
  },
  barcodeWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  humanReadableText: {
    marginTop: 6,
    letterSpacing: 2,
    textAlign: 'center',
  },
  fallbackContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    columnGap: 10,
  },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    backgroundColor: '#FFFFFF',
  },
  scanBtn: {
    borderWidth: 1,
  },
  actionBtnText: {
    marginLeft: 6,
  },
  skuSection: {
    marginTop: 14,
  },
  skuLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  autoBadge: {
    backgroundColor: '#E0F2FE',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 6,
  },
  manualBadge: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 6,
  },
  regenerateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.2,
    borderRadius: 8,
    paddingHorizontal: 10,
    height: 46,
  },
  inputLeftIcon: {
    marginRight: 6,
  },
  textInput: {
    flex: 1,
    fontSize: 14,
    paddingVertical: 0,
    height: '100%',
  },
  saveChip: {
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 6,
    marginLeft: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
  },
  errorText: {
    marginLeft: 4,
  },
  helperText: {
    marginTop: 5,
    fontStyle: 'italic',
  },
  // Fullscreen Scanner Modal Styles
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContent: {
    borderRadius: 16,
    padding: 20,
    alignItems: 'stretch',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.3,
        shadowRadius: 10,
      },
      android: {
        elevation: 8,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalCloseBtn: {
    padding: 4,
  },
  modalDivider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: 14,
  },
  modalBarcodeCanvas: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#111827',
    paddingVertical: 20,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 180,
  },
  modalCodeText: {
    marginTop: 12,
    letterSpacing: 3,
    textAlign: 'center',
  },
  modalMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 14,
    paddingHorizontal: 4,
  },
  modalActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 18,
  },
});

export default ProductBarcodeGenerator;
